import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { track } from "@/lib/analytics"
import type { Video, Script, Render, SocialConnection } from "@/types"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import PublishDialog from "./publish-dialog"
import CopyShareLinkButton from "./copy-share-link-button"
import SocialPostsList from "./social-posts-list"
import type { RenderProgressData } from "./wizard-step-render"

const FORMAT_LABELS: Record<Video["aspect_ratio"], string> = {
  portrait: "9:16 Portrait",
  landscape: "16:9 Landscape",
  square: "1:1 Square",
}

export default function VideoCompletedView({
  video,
  listing,
  renders,
  connections,
  scripts,
  onRender,
  isRendering,
  renderProgress,
  renderError,
}: {
  video: Video
  listing: { slug: string; address?: string; latitude?: number | string | null; longitude?: number | string | null }
  renders: Render[]
  connections: SocialConnection[]
  scripts: Script[]
  onRender: () => void
  isRendering: boolean
  renderProgress?: RenderProgressData
  renderError?: string | null
}) {
  const activeScript = scripts.find((s) => s.active)
  const completedRenders = renders.filter((r) => r.status === "completed")
  const latestRender = completedRenders[0] ?? null
  const previousRenders = completedRenders.slice(1)

  if (!latestRender) return null

  const videoSrc = latestRender.video_url || latestRender.output_url
  const isLandscape = video.aspect_ratio === "landscape"

  // Portrait/square: video at natural width, sidebar fills remaining space
  // Landscape: proportional 3/5 + 2/5 split since the video needs room
  const videoAspectClass: Record<Video["aspect_ratio"], string> = {
    portrait: "w-full aspect-[9/16]",
    landscape: "w-full aspect-video",
    square: "w-full aspect-square",
  }

  const hasSocialPosts = latestRender.social_posts && latestRender.social_posts.length > 0

  return (
    <div className={`flex flex-col gap-6 lg:flex-row lg:items-start ${isLandscape ? "" : "lg:gap-8"}`}>
      {/* Video column */}
      <div className={isLandscape ? "min-w-0 lg:flex-[3]" : "lg:w-80 lg:shrink-0"}>
        {/* Video player or rendering progress */}
        {isRendering && renderProgress ? (
          <CompactRenderProgress progress={renderProgress} onRetry={onRender} />
        ) : videoSrc ? (
          <div className={`${videoAspectClass[video.aspect_ratio]} overflow-hidden rounded-xl bg-charcoal shadow-xl shadow-charcoal/15`}>
            <video
              src={videoSrc}
              controls
              className="size-full object-contain"
              preload="metadata"
            />
          </div>
        ) : null}

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          {/* Primary: Post to socials */}
          <PublishDialog
            videoId={video.id}
            render={latestRender}
            connections={connections || []}
            activeScript={activeScript}
            onPublished={() => router.reload()}
            size="default"
            variant="red"
            label="Post"
            listingLat={listing.latitude}
            listingLng={listing.longitude}
          />

          {/* Secondary icon actions */}
          <div className="flex items-center gap-1.5">
            <TooltipProvider>
              {videoSrc && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild variant="secondary" size="icon">
                      <a href={videoSrc} download onClick={() => track("video_downloaded", { render_id: latestRender.id })}>
                        <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Download MP4</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <CopyShareLinkButton videoId={video.id} size="icon" iconOnly />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">Copy share link</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={onRender}
                    disabled={isRendering}
                  >
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
                    </svg>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {isRendering ? "Starting..." : "Render again"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {renderError && (
          <p className="mt-3 text-sm text-red">{renderError}</p>
        )}

        {/* Social posts — only in landscape left column */}
        {isLandscape && hasSocialPosts && (
          <SocialPostsList
            posts={latestRender.social_posts}
            videoId={video.id}
            renderId={latestRender.id}
            onPostRemoved={() => router.reload()}
          />
        )}
      </div>

      {/* Sidebar column */}
      <div className={`space-y-6 ${isLandscape ? "lg:flex-[2]" : "min-w-0 lg:flex-1"}`}>
        {/* Details */}
        <div>
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Details
          </h3>
          <div className="space-y-3 rounded-xl border border-stone/30 bg-white px-4 py-3">
            <DetailRow label="Format" value={FORMAT_LABELS[video.aspect_ratio]} />
            <DetailRow label="Template" value={capitalise(video.template_variant)} />
            <DetailRow label="Transitions" value={capitalise(video.transition_style)} />
            {latestRender.duration_ms && (
              <DetailRow label="Duration" value={`${Math.round(latestRender.duration_ms / 1000)}s`} />
            )}
            <DetailRow
              label="Rendered"
              value={new Date(latestRender.created_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            />
          </div>
        </div>

        {/* Links */}
        <div>
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Links
          </h3>
          <div className="space-y-3 rounded-xl border border-stone/30 bg-white px-4 py-3">
            <Link
              href={`/dashboard/listings/${listing.slug}`}
              className="flex items-center gap-2 text-sm font-medium text-charcoal hover:text-red"
            >
              <svg className="size-4 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
              </svg>
              View listing
            </Link>
            <a
              href={`/v/${video.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-charcoal hover:text-red"
            >
              <svg className="size-4 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              Share page
            </a>
          </div>
        </div>

        {/* Social posts — in sidebar for portrait/square */}
        {!isLandscape && hasSocialPosts && (
          <SocialPostsList
            posts={latestRender.social_posts}
            videoId={video.id}
            renderId={latestRender.id}
            onPostRemoved={() => router.reload()}
          />
        )}

        {/* Previous renders */}
        {previousRenders.length > 0 && (
          <PreviousRendersList videoId={video.id} renders={previousRenders} />
        )}
      </div>
    </div>
  )
}

function PreviousRendersList({ videoId, renders }: { videoId: string; renders: Render[] }) {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  function handleDelete(renderId: string) {
    setDeletingId(renderId)
    router.delete(`/dashboard/videos/${videoId}/renders/${renderId}`, {
      preserveScroll: true,
      onFinish: () => setDeletingId(null),
    })
  }

  return (
    <div>
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
        Previous renders
      </h3>
      <div className="space-y-2">
        {renders.map((r) => {
          const url = r.video_url || r.output_url
          return (
            <div
              key={r.id}
              className="group flex items-center justify-between rounded-xl border border-stone/30 bg-white px-4 py-3"
            >
              <div className="flex items-center gap-2.5">
                <RenderStatusDot status={r.status} />
                <span className="text-xs text-warm-grey capitalize">
                  {r.template_variant} &middot; {r.transition_style}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-warm-grey/60">
                  {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                </span>
                {url && (
                  <a
                    href={url}
                    download
                    className="text-[11px] font-medium text-red hover:underline"
                    onClick={() => track("video_downloaded", { render_id: r.id })}
                  >
                    Download
                  </a>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      disabled={deletingId === r.id}
                      className="rounded p-0.5 text-warm-grey/40 opacity-0 transition-all hover:text-red group-hover:opacity-100 disabled:opacity-50"
                      aria-label="Delete render"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent size="sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete render?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this render. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction variant="destructive" onClick={() => handleDelete(r.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CompactRenderProgress({
  progress,
  onRetry,
}: {
  progress: RenderProgressData
  onRetry: () => void
}) {
  const isFailed = progress.status === "failed"
  const pct = progress.status === "completed" ? 100 : progress.progress

  const stepLabels: Record<string, string> = {
    preparing: "Preparing",
    rendering: "Rendering",
    finalising: "Finalising",
  }

  return (
    <div className="rounded-xl border border-stone/30 bg-white px-5 py-4">
      <div className="flex items-center gap-3">
        {isFailed ? (
          <svg className="size-4 shrink-0 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="size-4 shrink-0 animate-spin text-red" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <p className={`text-sm font-medium ${isFailed ? "text-red" : "text-charcoal"}`}>
              {isFailed ? "Render failed" : stepLabels[progress.step] ?? progress.step}
            </p>
            {!isFailed && (
              <span className="text-xs tabular-nums text-warm-grey">{Math.round(pct)}%</span>
            )}
          </div>
          {!isFailed && (
            <div className="mt-1.5 overflow-hidden rounded-full bg-stone/15">
              <div
                className="h-1.5 rounded-full bg-red transition-all duration-700 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      </div>
      {isFailed && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 text-sm font-medium text-red hover:underline"
        >
          Try again
        </button>
      )}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-warm-grey">{label}</span>
      <span className="text-sm font-medium text-charcoal">{value}</span>
    </div>
  )
}

function RenderStatusDot({ status }: { status: Render["status"] }) {
  const colours: Record<Render["status"], string> = {
    pending: "bg-stone/40",
    rendering: "bg-amber-400",
    completed: "bg-charcoal",
    failed: "bg-red",
    timed_out: "bg-red",
  }
  return <span className={`inline-block size-2 rounded-full ${colours[status]}`} />
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
